"use client"

import * as React from 'react'
import Link from 'next/link'
import { useRouter } from 'next/navigation'
import { z } from 'zod'
import { Page, PageBody } from '@open-mercato/ui/backend/Page'
import { CrudForm } from '@open-mercato/ui/backend/CrudForm'
import { Button } from '@open-mercato/ui/primitives/button'
import { useConfirmDialog } from '@open-mercato/ui/backend/confirm-dialog'
import { LoadingMessage, ErrorMessage } from '@open-mercato/ui/backend/detail'
import { apiCall, apiCallOrThrow } from '@open-mercato/ui/backend/utils/apiCall'
import { deleteCrud } from '@open-mercato/ui/backend/utils/crud'
import { flash } from '@open-mercato/ui/backend/FlashMessages'
import { useT } from '@open-mercato/shared/lib/i18n/context'

type DetailProps = {
  params: {
    id: string
  }
}

type CargoAssignment = {
  id: string
  salesShipmentId?: string | null
  executionReferenceKind?: string | null
  executionReferenceId?: string | null
  relationship: string
  notes?: string | null
}

type CargoHistoryItem = {
  id: string
  eventType: string
  mode?: string | null
  source?: string | null
  notes?: string | null
  happenedAt?: string | null
  location?: {
    label?: string | null
    latitude?: number | null
    longitude?: number | null
  } | null
}

type CargoDetail = {
  id: string
  createdByUserId?: string | null
  canManage?: boolean
  cargoType: string
  referenceNumber: string
  description?: string | null
  containerNumber?: string | null
  containerSizeType?: string | null
  sealNumber?: string | null
  gs1Sscc?: string | null
  palletCount?: number | null
  palletType?: string | null
  bulkMaterial?: string | null
  bulkUnit?: string | null
  bulkQuantity?: number | null
  currentMode?: string | null
  currentLocation?: {
    label?: string | null
    latitude?: number | null
    longitude?: number | null
    source?: string | null
  } | null
  lastEventAt?: string | null
  assignments?: CargoAssignment[]
  history?: CargoHistoryItem[]
}

type LocationFormValues = {
  currentMode?: string
  locationLabel?: string
  locationLatitude?: number | string
  locationLongitude?: number | string
  source?: string
  notes?: string
}

const toNullableText = (value: unknown): string | null => {
  if (typeof value !== 'string') return null
  const trimmed = value.trim()
  return trimmed.length ? trimmed : null
}

const toNullableNumber = (value: unknown): number | null => {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null
  if (typeof value === 'string') {
    const trimmed = value.trim()
    if (!trimmed.length) return null
    const parsed = Number(trimmed)
    return Number.isFinite(parsed) ? parsed : null
  }
  return null
}

const locationFormSchema = z.object({
  currentMode: z.string().optional(),
  locationLabel: z.string().optional(),
  locationLatitude: z.union([z.string(), z.number()]).optional(),
  locationLongitude: z.union([z.string(), z.number()]).optional(),
  source: z.string().optional(),
  notes: z.string().optional(),
})

const formatLocation = (location: CargoDetail['currentLocation'] | CargoHistoryItem['location']) => {
  if (!location) return null
  if (location.label) return location.label
  if (location.latitude !== null && location.latitude !== undefined && location.longitude !== null && location.longitude !== undefined) {
    return `${location.latitude.toFixed(4)}, ${location.longitude.toFixed(4)}`
  }
  return null
}

export default function CargoDetailPage({ params }: DetailProps) {
  const t = useT()
  const router = useRouter()
  const cargoId = params.id
  const [cargo, setCargo] = React.useState<CargoDetail | null>(null)
  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState<string | null>(null)
  const [deleting, setDeleting] = React.useState(false)
  const { confirm, ConfirmDialogElement } = useConfirmDialog()

  const loadCargo = React.useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const response = await apiCall<CargoDetail>(`/api/freight/cargo/${cargoId}`)
      if (!response.ok || !response.result) {
        throw new Error(t('cargo.pages.detail.loadError', 'Failed to load cargo.'))
      }
      setCargo(response.result)
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : t('cargo.pages.detail.loadError', 'Failed to load cargo.'))
    } finally {
      setLoading(false)
    }
  }, [cargoId, t])

  React.useEffect(() => {
    void loadCargo()
  }, [loadCargo])

  const locationFields = React.useMemo(
    () => [
      {
        id: 'currentMode',
        label: t('cargo.form.fields.currentMode', 'Current mode'),
        type: 'select' as const,
        options: [
          { value: 'road', label: t('cargo.modes.road', 'Road') },
          { value: 'sea', label: t('cargo.modes.sea', 'Sea') },
          { value: 'rail', label: t('cargo.modes.rail', 'Rail') },
          { value: 'air', label: t('cargo.modes.air', 'Air') },
          { value: 'warehouse', label: t('cargo.modes.warehouse', 'Warehouse') },
          { value: 'terminal', label: t('cargo.modes.terminal', 'Terminal') },
        ],
      },
      {
        id: 'locationLabel',
        label: t('cargo.form.fields.currentLocationLabel', 'Location label'),
        type: 'text' as const,
      },
      {
        id: 'locationLatitude',
        label: t('cargo.form.fields.currentLocationLatitude', 'Latitude'),
        type: 'number' as const,
      },
      {
        id: 'locationLongitude',
        label: t('cargo.form.fields.currentLocationLongitude', 'Longitude'),
        type: 'number' as const,
      },
      {
        id: 'source',
        label: t('cargo.form.fields.currentLocationSource', 'Location source'),
        type: 'text' as const,
      },
      {
        id: 'notes',
        label: t('cargo.pages.detail.locationNotes', 'Update notes'),
        type: 'textarea' as const,
      },
    ],
    [t],
  )

  const initialLocationValues = React.useMemo<Partial<LocationFormValues>>(
    () => ({
      currentMode: cargo?.currentMode ?? '',
      locationLabel: cargo?.currentLocation?.label ?? '',
      locationLatitude: cargo?.currentLocation?.latitude ?? '',
      locationLongitude: cargo?.currentLocation?.longitude ?? '',
      source: cargo?.currentLocation?.source ?? '',
      notes: '',
    }),
    [cargo],
  )

  return (
    <Page>
      <PageBody>
        <div className="space-y-6">
          <div className="flex items-center justify-between gap-4">
            <div>
              <div className="text-sm text-muted-foreground">{t(`cargo.types.${cargo?.cargoType ?? 'general'}`, cargo?.cargoType ?? 'general')}</div>
              <h1 className="text-2xl font-semibold">{cargo?.referenceNumber ?? t('cargo.pages.detail.title', 'Cargo detail')}</h1>
            </div>
            <div className="flex items-center gap-2">
              {cargo?.canManage ? (
                <>
                  <Button asChild variant="outline">
                    <Link href={`/backend/cargo/${cargoId}/edit`}>{t('cargo.pages.edit.action', 'Edit cargo')}</Link>
                  </Button>
                  <Button
                    type="button"
                    variant="destructive"
                    onClick={async () => {
                      const confirmed = await confirm({
                        title: t('cargo.pages.detail.deleteTitle', 'Delete cargo'),
                        text: t('cargo.pages.detail.deleteConfirm', 'This cargo will be removed from active operations.'),
                        confirmText: t('cargo.pages.detail.deleteAction', 'Delete'),
                        variant: 'destructive',
                      })
                      if (!confirmed) return
                      setDeleting(true)
                      try {
                        await deleteCrud(`freight/cargo/${cargoId}`, {
                          errorMessage: t('cargo.pages.detail.deleteError', 'Failed to delete cargo.'),
                        })
                        flash(t('cargo.pages.detail.deleteSuccess', 'Cargo deleted.'), 'success')
                        router.push('/backend/cargo')
                      } finally {
                        setDeleting(false)
                      }
                    }}
                    disabled={deleting}
                  >
                    {deleting ? t('cargo.pages.detail.deleting', 'Deleting…') : t('cargo.pages.detail.deleteAction', 'Delete')}
                  </Button>
                </>
              ) : null}
              <Button asChild variant="outline">
                <Link href="/backend/cargo">{t('cargo.pages.detail.back', 'Back to cargo')}</Link>
              </Button>
            </div>
          </div>

          {loading ? <LoadingMessage label={t('cargo.pages.detail.loading', 'Loading cargo…')} /> : null}
          {error ? <ErrorMessage label={error} /> : null}

          {!loading && !error && cargo ? (
            <div className="space-y-6">
              <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                <div className="rounded-xl border p-4">
                  <div className="text-xs uppercase tracking-wide text-muted-foreground">{t('cargo.pages.detail.currentMode', 'Current mode')}</div>
                  <div className="mt-2 text-lg font-medium">
                    {cargo.currentMode ? t(`cargo.modes.${cargo.currentMode}`, cargo.currentMode) : t('cargo.state.modeUnknown', 'Mode unknown')}
                  </div>
                </div>
                <div className="rounded-xl border p-4">
                  <div className="text-xs uppercase tracking-wide text-muted-foreground">{t('cargo.pages.detail.currentLocation', 'Current location')}</div>
                  <div className="mt-2 text-lg font-medium">
                    {formatLocation(cargo.currentLocation) ?? t('cargo.state.locationUnknown', 'Location unknown')}
                  </div>
                </div>
                <div className="rounded-xl border p-4">
                  <div className="text-xs uppercase tracking-wide text-muted-foreground">{t('cargo.pages.detail.lastEventAt', 'Last event')}</div>
                  <div className="mt-2 text-lg font-medium">
                    {cargo.lastEventAt ? new Date(cargo.lastEventAt).toLocaleString() : t('cargo.state.noEvents', 'No events yet')}
                  </div>
                </div>
                <div className="rounded-xl border p-4">
                  <div className="text-xs uppercase tracking-wide text-muted-foreground">{t('cargo.pages.detail.links', 'Links')}</div>
                  <div className="mt-2 text-lg font-medium">
                    {cargo.assignments?.length ?? 0}
                  </div>
                </div>
              </div>

              <div className="grid gap-6 xl:grid-cols-[1.2fr_0.8fr]">
                <div className="space-y-6">
                  <div className="rounded-xl border p-4">
                    <h2 className="text-lg font-medium">{t('cargo.pages.detail.summary', 'Cargo summary')}</h2>
                    <div className="mt-4 grid gap-3 text-sm text-muted-foreground md:grid-cols-2">
                      <div>{cargo.containerNumber ?? cargo.palletType ?? cargo.bulkMaterial ?? t('cargo.labels.noSubtype', 'No subtype details')}</div>
                      <div>{cargo.containerSizeType ?? cargo.bulkUnit ?? cargo.gs1Sscc ?? t('cargo.labels.noIdentifier', 'No extra identifier')}</div>
                      <div>{cargo.description ?? t('cargo.labels.noDescription', 'No description')}</div>
                      <div>{cargo.sealNumber ?? t('cargo.labels.noSeal', 'No seal number')}</div>
                    </div>
                  </div>

                  <div className="rounded-xl border p-4">
                    <h2 className="text-lg font-medium">{t('cargo.pages.detail.history', 'Location history')}</h2>
                    <div className="mt-4 space-y-3">
                      {cargo.history?.length ? cargo.history.map((entry) => (
                        <div key={entry.id} className="rounded-lg border bg-muted/20 p-3 text-sm">
                          <div className="flex flex-wrap items-center justify-between gap-2">
                            <div className="font-medium">{formatLocation(entry.location) ?? t('cargo.state.locationUnknown', 'Location unknown')}</div>
                            <div className="text-muted-foreground">
                              {entry.happenedAt ? new Date(entry.happenedAt).toLocaleString() : t('cargo.state.noEvents', 'No events yet')}
                            </div>
                          </div>
                          <div className="mt-1 text-muted-foreground">
                            {entry.mode ? t(`cargo.modes.${entry.mode}`, entry.mode) : t('cargo.state.modeUnknown', 'Mode unknown')}
                            {entry.source ? ` • ${entry.source}` : ''}
                            {entry.notes ? ` • ${entry.notes}` : ''}
                          </div>
                        </div>
                      )) : (
                        <div className="text-sm text-muted-foreground">
                          {t('cargo.pages.detail.historyEmpty', 'No location history available yet.')}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="space-y-6">
                  <div className="rounded-xl border p-4">
                    <h2 className="text-lg font-medium">{t('cargo.pages.detail.assignments', 'Assignments')}</h2>
                    <div className="mt-4 space-y-3">
                      {cargo.assignments?.length ? cargo.assignments.map((assignment) => (
                        <div key={assignment.id} className="rounded-lg border bg-muted/20 p-3 text-sm">
                          <div className="font-medium">{assignment.relationship}</div>
                          <div className="mt-1 text-muted-foreground">
                            {assignment.salesShipmentId ? `Shipment: ${assignment.salesShipmentId}` : null}
                            {assignment.executionReferenceKind && assignment.executionReferenceId
                              ? ` • ${assignment.executionReferenceKind}: ${assignment.executionReferenceId}`
                              : ''}
                          </div>
                          {assignment.notes ? <div className="mt-1 text-muted-foreground">{assignment.notes}</div> : null}
                        </div>
                      )) : (
                        <div className="text-sm text-muted-foreground">
                          {t('cargo.pages.detail.assignmentsEmpty', 'This cargo is not linked to any shipment or execution record yet.')}
                        </div>
                      )}
                    </div>
                  </div>

                  {cargo.canManage ? (
                    <div className="rounded-xl border p-4">
                      <CrudForm<LocationFormValues>
                        title={t('cargo.pages.detail.updateLocation', 'Update live location')}
                        fields={locationFields}
                        initialValues={initialLocationValues}
                        schema={locationFormSchema}
                        submitLabel={t('cargo.pages.detail.updateLocationSubmit', 'Save location update')}
                        cancelHref={`/backend/cargo/${cargoId}`}
                        onSubmit={async (values) => {
                          const locationLabel = toNullableText(values.locationLabel)
                          const locationLatitude = toNullableNumber(values.locationLatitude)
                          const locationLongitude = toNullableNumber(values.locationLongitude)
                          const source = toNullableText(values.source)
                          const payload = {
                            currentMode: toNullableText(values.currentMode),
                            currentLocation:
                              locationLabel || locationLatitude !== null || locationLongitude !== null || source
                                ? {
                                    label: locationLabel,
                                    latitude: locationLatitude,
                                    longitude: locationLongitude,
                                    source,
                                  }
                                : undefined,
                            source,
                            notes: toNullableText(values.notes),
                          }
                          await apiCallOrThrow(`/api/freight/cargo/${cargoId}/location`, {
                            method: 'PATCH',
                            headers: { 'content-type': 'application/json' },
                            body: JSON.stringify(payload),
                          }, {
                            errorMessage: t('cargo.pages.detail.updateLocationError', 'Failed to update location.'),
                          })
                          flash(t('cargo.pages.detail.updateLocationSuccess', 'Cargo location updated.'), 'success')
                          await loadCargo()
                        }}
                      />
                    </div>
                  ) : (
                    <div className="rounded-xl border p-4 text-sm text-muted-foreground">
                      {t('cargo.pages.detail.ownerOnlyManage', 'Only the cargo creator can edit, move, or delete this record.')}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ) : null}
        </div>
        {ConfirmDialogElement}
      </PageBody>
    </Page>
  )
}
